<template>
  <homepage-main />
</template>

<script setup lang="ts">
import { computed, nextTick, onDeactivated, onUnmounted } from 'vue'
import { getLangFallbackUrl } from '~/lang/utils/langfetch'
import { fetchWithCache } from '~/utility/fetchWithCache'
import { getHttpHeaders } from '~/utility/headers'
import SCHEMA_ORGANIZATION from '../json/schemaOrganizationWithAggregateRating.json'

export type PitchupStats = {
  campsiteReviewsCount: number
  campsitePhotosCount: number
  campsitesBookableCount: number
  campsitesActiveCount: number
}

definePageMeta({
  layout: 'homepage-layout', // Use homepage-layout at build time instead of runtime
  httpHeaders: (to) => getHttpHeaders({ to }),
})

const runtimeConfig = useRuntimeConfig()
const route = useRoute()
const { $i18n, $isDesktop } = useNuxtApp()

const { data: pitchupStats } = await useAsyncData(
  'index-pitchup-stats',
  async () => {
    const langCode = (useRoute().params?.lang as string) || 'en-gb'
    const url = getLangFallbackUrl(langCode, '/_/endpoints/pitchup-stats/')
    return await fetchWithCache<PitchupStats[]>({ url })
  },
)

const headData = computed(() => {
  const urlWithoutParams = new URL(
    `https://${runtimeConfig.public.envUrl}${route.fullPath}`,
  )
  urlWithoutParams.searchParams.delete('hierarchyPath')
  urlWithoutParams.searchParams.delete('facet')

  return {
    title:
      ($i18n.$t('Camping, Glamping, Caravan and Holiday Parks') as string) +
      ' | Pitchup.com',
    meta: [
      {
        hid: 'og:title',
        property: 'og:title',
        content: $i18n.$t(
          'Camping, Glamping, Caravan and Holiday Parks',
        ) as string,
      },
      {
        hid: 'description',
        name: 'description',
        property: 'og:description',
        content: $i18n.$t(
          'Book campsites, caravan sites and glamping from the specialists at Pitchup.com, with over {campsitesActiveCount, number, integer} listings and {reviewsCount, number, integer} independent reviews your next holiday awaits you.',
          {
            campsitesActiveCount: pitchupStats?.campsitesActiveCount || 14000,
            reviewsCount: pitchupStats?.campsiteReviewsCount || 730000,
          },
        ) as string,
      },
      {
        hid: 'og:url',
        property: 'og:url',
        content: urlWithoutParams.toString(),
      },
      {
        hid: 'twitter:image',
        name: 'twitter:image',
        content: `https://${runtimeConfig.public.envUrl}${route.path}app-icons/pitchup_logo_large.png`,
      },
    ],
    bodyAttrs: {
      class: $isDesktop ? 'homepage desktop' : 'homepage',
    },
    __dangerouslyDisableSanitizers: ['script', 'innerHTML'],
    script: [
      {
        innerHTML: $isDesktop
          ? ''
          : `
            document.getElementById('autosuggest__input')?.addEventListener('click', () => {
              setTimeout(() => {
                document.body.classList.add('homepage-search-open', 'active-tab-search')
              })
            }, {
              capture: true,
              passive: true
            })
          `,
        tagPosition: 'bodyClose',
      },
      {
        type: 'application/ld+json',
        body: true,
        innerHTML: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: 'Pitchup',
          url: 'https://www.pitchup.com/',
        }),
      },
      {
        type: 'application/ld+json',
        body: true,
        innerHTML: JSON.stringify(SCHEMA_ORGANIZATION),
        hid: 'schema-organization',
      },
    ],
  }
})

useHead(headData)

async function removeClass() {
  if (nextTick) {
    await nextTick()
  }
  if (
    document &&
    document.body &&
    document.body.classList &&
    document.body.classList.remove
  ) {
    document.body.classList.remove('homepage-search-open', 'active-tab-search')
  }
}

onDeactivated(async () => {
  if (removeClass) {
    await removeClass()
  }
})

onUnmounted(async () => {
  if (removeClass) {
    await removeClass()
  }
})
</script>
<style>
[data-homepage='true'] .app-footer {
  padding-top: 2em;
  min-height: 10em;
}

[data-homepage='true'] footer .feefo-badge,
[data-homepage='true'] .menu-btn img {
  display: none;
}

[data-homepage='true'] .active-tab-search .menu-btn img,
[data-homepage='true'] .homepage-search-open .menu-btn img {
  display: block;

  border: 1px solid orange;
}

[data-homepage='true']
  .homepage-search-open
  .search-banner-wrapper
  .search-form {
  padding: 1.4em;
  background: linear-gradient(white, #f1f0f4 15%);
  height: 100dvh;
}

[data-homepage='true'] .homepage-search-open .accommodation-wizard,
[data-homepage='true'] .homepage-search-open .dates-wizard,
[data-homepage='true'] .homepage-search-open .guests-wizard,
[data-homepage='true']
  .homepage-search-open
  .campsite-search-wizard-main-actions,
[data-homepage='true'] .homepage-search-open .location-section .help-text,
[data-homepage='true']
  .homepage-search-open
  .location-section
  .location-options {
  display: block;
}

[data-homepage='true'] .homepage-search-open .homepage-background {
  display: none;
}

[data-homepage='true'] .homepage-search-open .homepage-logo {
  display: none;
}

[data-homepage='true'] .homepage-search-open .banner-link {
  display: none;
}
</style>
